import { useState, useEffect } from 'react';
import { Settings, Save, Store, Clock, Percent, Truck, CreditCard, Shield, MapPin, Plus, Trash2, Edit, Check, X, ArrowLeft, Upload, HelpCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

const defaultConfig = {
  store_name: 'Keiks Reposteria',
  phone: '573012280877',
  whatsapp_phone: '573012280877',
  whatsapp_floating_phone: '573012280877',
  email: 'info@keiks.com',
  address: 'Bogota, Colombia',
  deposit_percentage: 50,
  min_order: 0,
  delivery_enabled: true,
  delivery_fee: 10000,
  whatsapp_enabled: true,
  rappi_enabled: false,
  payment_transfer: true,
  bank_name: 'Bancolombia',
  bank_account_type: 'Cuenta de Ahorros',
  bank_account_number: '123-456789-01',
  bank_account_holder: 'Keiks Repostería S.A.S',
  payment_cash: true,
  opening_hours: 'Lun-Sab: 8am-8pm | Dom: 9am-6pm',
  active_gateway: 'none',
  accept_online_payments: true,
  wompi_mode: 'sandbox',
  wompi_public_key_sandbox: '',
  wompi_private_key_sandbox: '',
  wompi_public_key_production: '',
  wompi_private_key_production: '',
  epayco_mode: 'sandbox',
  epayco_public_key_sandbox: '',
  epayco_private_key_sandbox: '',
  epayco_public_key_production: '',
  epayco_private_key_production: '',
  banner_images: [],
  banners: [],
};

const compressImage = (file, maxWidth = 800) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", { type: 'image/jpeg', lastModified: Date.now() }));
            } else {
              reject(new Error('Compression failed'));
            }
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('general'); // 'general' | 'zones' | 'banners'
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Delivery Zones States
  const [zones, setZones] = useState([]);
  const [stores, setStores] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [isAddingZone, setIsAddingZone] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState({
    name: '',
    route: '',
    store_id: '',
    store_name: '',
    schedule_9_12: true,
    schedule_12_3: true,
    schedule_3_6: true,
    delivery_creative: 13900,
    delivery_normal: 8900,
  });

  // Banners States
  const [editingBannerIndex, setEditingBannerIndex] = useState(null); // null = not editing, -1 = new banner, >=0 = editing index
  const [bannerForm, setBannerForm] = useState({
    image: '',
    title: '',
    subtitle: '',
    cta_text: '',
    cta_link: '',
    is_jumbotron: false
  });

  // Complements States
  const [editingComplementIndex, setEditingComplementIndex] = useState(null);
  const [complementForm, setComplementForm] = useState({
    name: '',
    price: 0,
    image: '',
    allow_color: false,
    allow_quantity: true
  });

  // Flavors and Fillings States
  const [editingFlavorIndex, setEditingFlavorIndex] = useState(null);
  const [flavorForm, setFlavorForm] = useState({ name: '', price: 0, is_refrigerated: false });
  const [editingFillingIndex, setEditingFillingIndex] = useState(null);
  const [fillingForm, setFillingForm] = useState({ name: '', price: 0 });

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { withCredentials: true, headers: getHeaders() });
      if (data && Object.keys(data).length > 0) {
        setConfig(prev => ({ ...prev, ...data, banner_images: data.banner_images || [], banners: data.banners || [], complements: data.complements || [], flavors: data.flavors || [], fillings: data.fillings || [] }));
      }
    } catch {}
    finally { setLoading(false); }
  };

  const fetchStores = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stores`, { withCredentials: true, headers: getHeaders() });
      setStores(data);
      if (data.length > 0 && !zoneForm.store_id) {
        setZoneForm(prev => ({ ...prev, store_id: data[0].id, store_name: data[0].name }));
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  const fetchZones = async () => {
    setZonesLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/delivery-zones`, { withCredentials: true, headers: getHeaders() });
      setZones(data);
    } catch (err) {
      console.error('Error fetching zones:', err);
    } finally {
      setZonesLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchStores();
    fetchZones();
  }, []);

  const handleSaveConfig = async (newConfig = config) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, newConfig, { withCredentials: true, headers: getHeaders() });
      toast.success('Configuración guardada');
      setConfig(newConfig);
    } catch { toast.error('Error al guardar la configuración'); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  // Zones handlers
  const handleZoneFormChange = (field, value) => {
    if (field === 'store_id') {
      const matchingStore = stores.find(s => s.id === value);
      setZoneForm(prev => ({ ...prev, store_id: value, store_name: matchingStore ? matchingStore.name : '' }));
    } else {
      setZoneForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const openAddZone = () => {
    setEditingZone(null);
    setZoneForm({
      name: '',
      route: '',
      store_id: stores.length > 0 ? stores[0].id : '',
      store_name: stores.length > 0 ? stores[0].name : '',
      schedule_9_12: true,
      schedule_12_3: true,
      schedule_3_6: true,
      delivery_creative: 13900,
      delivery_normal: 8900,
    });
    setIsAddingZone(true);
  };

  const openEditZone = (zone) => {
    setEditingZone(zone);
    setZoneForm({
      name: zone.name,
      route: zone.route,
      store_id: zone.store_id,
      store_name: zone.store_name,
      schedule_9_12: zone.schedule_9_12 ?? true,
      schedule_12_3: zone.schedule_12_3 ?? true,
      schedule_3_6: zone.schedule_3_6 ?? true,
      delivery_creative: zone.delivery_creative || 13900,
      delivery_normal: zone.delivery_normal || 8900,
    });
    setIsAddingZone(true);
  };

  const saveZone = async (e) => {
    e.preventDefault();
    if (!zoneForm.name || !zoneForm.store_id) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    try {
      if (editingZone) {
        await axios.put(`${API}/admin/delivery-zones/${editingZone.id}`, zoneForm, { withCredentials: true, headers: getHeaders() });
        toast.success('Zona actualizada correctamente');
      } else {
        await axios.post(`${API}/admin/delivery-zones`, zoneForm, { withCredentials: true, headers: getHeaders() });
        toast.success('Zona creada correctamente');
      }
      setIsAddingZone(false);
      setEditingZone(null);
      fetchZones();
    } catch {
      toast.error('Error al guardar la zona');
    }
  };

  const deleteZone = async (zoneId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta zona de envío?')) return;
    try {
      await axios.delete(`${API}/admin/delivery-zones/${zoneId}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Zona eliminada');
      fetchZones();
    } catch {
      toast.error('Error al eliminar la zona');
    }
  };

  // Banner Upload
  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const toastId = toast.loading('Subiendo imagen del banner...');
    try {
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        try {
          fileToUpload = await compressImage(file, 1200);
        } catch {}
      }
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      const updatedBanners = [...(config.banner_images || []), data.url];
      const updatedConfig = { ...config, banner_images: updatedBanners };
      toast.dismiss(toastId);
      handleSaveConfig(updatedConfig);
    } catch (err) {
      toast.dismiss(toastId);
      toast.error('Error al subir la imagen');
      console.error(err);
    }
  };

  const removeBannerImage = (index) => {
    const updated = (config.banner_images || []).filter((_, i) => i !== index);
    const updatedConfig = { ...config, banner_images: updated };
    handleSaveConfig(updatedConfig);
  };

  // New Full Banners Handlers
  const handleBannerFormImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const toastId = toast.loading('Subiendo imagen del banner...');
    try {
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        try {
          fileToUpload = await compressImage(file, 1200);
        } catch {}
      }
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      setBannerForm(prev => ({ ...prev, image: data.url }));
      toast.dismiss(toastId);
      toast.success('Imagen subida correctamente');
    } catch (err) {
      toast.dismiss(toastId);
      toast.error('Error al subir la imagen');
      console.error(err);
    }
  };

  // Complements Handlers
  const handleComplementImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const toastId = toast.loading('Subiendo imagen del complemento...');
    try {
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        try {
          fileToUpload = await compressImage(file, 600);
        } catch {}
      }
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      setComplementForm(prev => ({ ...prev, image: data.url }));
      toast.dismiss(toastId);
      toast.success('Imagen subida correctamente');
    } catch (err) {
      toast.dismiss(toastId);
      toast.error('Error al subir la imagen');
      console.error(err);
    }
  };

  const openAddComplement = () => {
    setComplementForm({
      name: '',
      price: 0,
      image: '',
      allow_color: false,
      allow_quantity: true
    });
    setEditingComplementIndex(-1);
  };

  const openEditComplement = (idx) => {
    const comp = config.complements?.[idx] || {};
    setComplementForm({
      name: comp.name || '',
      price: comp.price || 0,
      image: comp.image || '',
      allow_color: comp.allow_color || false,
      allow_quantity: comp.allow_quantity !== false
    });
    setEditingComplementIndex(idx);
  };

  const saveComplement = async (e) => {
    e.preventDefault();
    if (!complementForm.name) {
      toast.error('Especifica el nombre del complemento');
      return;
    }
    const newComplements = [...(config.complements || [])];
    if (editingComplementIndex === -1) {
      newComplements.push(complementForm);
    } else {
      newComplements[editingComplementIndex] = complementForm;
    }
    const newConfig = { ...config, complements: newComplements };
    await handleSaveConfig(newConfig);
    setEditingComplementIndex(null);
  };

  const deleteComplement = async (idx) => {
    if (!window.confirm('¿Eliminar este complemento?')) return;
    const newComplements = (config.complements || []).filter((_, i) => i !== idx);
    const newConfig = { ...config, complements: newComplements };
    await handleSaveConfig(newConfig);
  };

  // Flavors Handlers
  const openAddFlavor = () => {
    setFlavorForm({ name: '', price: 0, is_refrigerated: false });
    setEditingFlavorIndex(-1);
  };
  const openEditFlavor = (idx) => {
    const f = config.flavors?.[idx] || {};
    setFlavorForm({ name: f.name || '', price: f.price || 0, is_refrigerated: !!f.is_refrigerated });
    setEditingFlavorIndex(idx);
  };
  const saveFlavor = async (e) => {
    e.preventDefault();
    if (!flavorForm.name) return;
    const newFlavors = [...(config.flavors || [])];
    if (editingFlavorIndex === -1) {
      newFlavors.push(flavorForm);
    } else {
      newFlavors[editingFlavorIndex] = flavorForm;
    }
    const newConfig = { ...config, flavors: newFlavors };
    await handleSaveConfig(newConfig);
    setEditingFlavorIndex(null);
  };
  const deleteFlavor = async (idx) => {
    if (!window.confirm('¿Eliminar este sabor?')) return;
    const newFlavors = (config.flavors || []).filter((_, i) => i !== idx);
    await handleSaveConfig({ ...config, flavors: newFlavors });
  };

  // Fillings Handlers
  const openAddFilling = () => {
    setFillingForm({ name: '', price: 0 });
    setEditingFillingIndex(-1);
  };
  const openEditFilling = (idx) => {
    const fill = config.fillings?.[idx] || {};
    setFillingForm({ name: fill.name || '', price: fill.price || 0 });
    setEditingFillingIndex(idx);
  };
  const saveFilling = async (e) => {
    e.preventDefault();
    if (!fillingForm.name) return;
    const newFillings = [...(config.fillings || [])];
    if (editingFillingIndex === -1) {
      newFillings.push(fillingForm);
    } else {
      newFillings[editingFillingIndex] = fillingForm;
    }
    const newConfig = { ...config, fillings: newFillings };
    await handleSaveConfig(newConfig);
    setEditingFillingIndex(null);
  };
  const deleteFilling = async (idx) => {
    if (!window.confirm('¿Eliminar este relleno?')) return;
    const newFillings = (config.fillings || []).filter((_, i) => i !== idx);
    await handleSaveConfig({ ...config, fillings: newFillings });
  };

  const openAddBanner = () => {
    setBannerForm({
      image: '',
      title: '',
      subtitle: '',
      cta_text: '',
      cta_link: '',
      is_jumbotron: false
    });
    setEditingBannerIndex(-1);
  };

  const openEditBanner = (banner, idx) => {
    setBannerForm({
      image: banner.image || '',
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      cta_text: banner.cta_text || '',
      cta_link: banner.cta_link || '',
      is_jumbotron: banner.is_jumbotron || false
    });
    setEditingBannerIndex(idx);
  };

  const saveBanner = (e) => {
    e.preventDefault();
    if (!bannerForm.image) {
      toast.error('La imagen del banner es obligatoria');
      return;
    }
    
    let updatedBanners = [...(config.banners || [])];
    if (editingBannerIndex === -1) {
      updatedBanners.push(bannerForm);
    } else {
      updatedBanners[editingBannerIndex] = bannerForm;
    }

    const updatedConfig = { ...config, banners: updatedBanners };
    handleSaveConfig(updatedConfig);
    setEditingBannerIndex(null);
  };

  const removeBanner = (index) => {
    if (!window.confirm('¿Estás seguro de eliminar este banner?')) return;
    const updated = (config.banners || []).filter((_, i) => i !== index);
    const updatedConfig = { ...config, banners: updated };
    handleSaveConfig(updatedConfig);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#46371c]">Configuración</h1>
          <p className="font-body text-sm text-[#75664d] mt-1">Ajustes generales, zonas de domicilio y banners</p>
        </div>
        {activeTab === 'general' && (
          <Button onClick={() => handleSaveConfig()} disabled={saving} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        )}
      </div>

      {/* Tabs Menu */}
      <div className="flex border-b border-[#e5dfd3] mb-6 gap-2">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 font-body text-sm font-semibold border-b-2 transition-all ${activeTab === 'general' ? 'border-[#46371c] text-[#46371c]' : 'border-transparent text-[#75664d] hover:text-[#46371c]'}`}
        >
          Ajustes Generales
        </button>
        <button
          onClick={() => setActiveTab('zones')}
          className={`px-4 py-2 font-body text-sm font-semibold border-b-2 transition-all ${activeTab === 'zones' ? 'border-[#46371c] text-[#46371c]' : 'border-transparent text-[#75664d] hover:text-[#46371c]'}`}
          data-testid="tab-zones"
        >
          Zonas de Envío
        </button>
        <button
          onClick={() => setActiveTab('banners')}
          className={`px-4 py-2 font-body text-sm font-semibold border-b-2 transition-all ${activeTab === 'banners' ? 'border-[#46371c] text-[#46371c]' : 'border-transparent text-[#75664d] hover:text-[#46371c]'}`}
          data-testid="tab-banners"
        >
          Banners de Inicio
        </button>
        <button
          onClick={() => setActiveTab('complements')}
          className={`px-4 py-2 font-body text-sm font-semibold border-b-2 transition-all ${activeTab === 'complements' ? 'border-[#46371c] text-[#46371c]' : 'border-transparent text-[#75664d] hover:text-[#46371c]'}`}
          data-testid="tab-complements"
        >
          Complementos
        </button>
        <button
          onClick={() => setActiveTab('flavors_fillings')}
          className={`px-4 py-2 font-body text-sm font-semibold border-b-2 transition-all ${activeTab === 'flavors_fillings' ? 'border-[#46371c] text-[#46371c]' : 'border-transparent text-[#75664d] hover:text-[#46371c]'}`}
          data-testid="tab-flavors-fillings"
        >
          Sabores y Rellenos
        </button>
      </div>

      {/* Tab: General Settings */}
      {activeTab === 'general' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Store Info */}
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
            <CardContent className="p-6">
              <h3 className="font-heading text-lg font-semibold text-[#46371c] mb-4 flex items-center gap-2"><Store className="w-5 h-5" /> Datos de la Tienda</h3>
              <div className="space-y-4">
                <div>
                  <label className="font-body text-sm text-[#75664d] mb-1 block">Nombre</label>
                  <input value={config.store_name} onChange={e => updateField('store_name', e.target.value)} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="setting-store-name" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-body text-sm text-[#75664d] mb-1 block">Teléfono de Contacto</label>
                    <input value={config.phone || ''} onChange={e => updateField('phone', e.target.value)} placeholder="Ej: 573012280877" className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="setting-phone" />
                  </div>
                  <div>
                    <label className="font-body text-sm text-[#75664d] mb-1 block">Email</label>
                    <input value={config.email || ''} onChange={e => updateField('email', e.target.value)} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="setting-email" />
                  </div>
                </div>

                <div className="p-4 bg-[#eef6f2]/60 rounded-xl border border-[#a0d5ba]/40 space-y-3">
                  <p className="font-heading text-xs font-bold text-[#347a54] uppercase tracking-wide">
                    Configuración de WhatsApp para el Frontend
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="font-body text-xs font-semibold text-[#46371c] mb-1 block">
                        WhatsApp Sitio y Carrito
                      </label>
                      <input
                        value={config.whatsapp_phone || ''}
                        onChange={e => updateField('whatsapp_phone', e.target.value)}
                        placeholder="Ej: 573012280877"
                        className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] bg-white font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        data-testid="setting-whatsapp-phone"
                      />
                      <p className="text-[10px] text-[#75664d] mt-1">Usado en checkout, carrito, footer y catálogo.</p>
                    </div>
                    <div>
                      <label className="font-body text-xs font-semibold text-[#46371c] mb-1 block">
                        WhatsApp Botón Flotante
                      </label>
                      <input
                        value={config.whatsapp_floating_phone || ''}
                        onChange={e => updateField('whatsapp_floating_phone', e.target.value)}
                        placeholder="Ej: 573012280877"
                        className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] bg-white font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        data-testid="setting-whatsapp-floating-phone"
                      />
                      <p className="text-[10px] text-[#75664d] mt-1">Número para el botón flotante (si se deja vacío, usa el del sitio).</p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="font-body text-sm text-[#75664d] mb-1 block">Dirección</label>
                  <input value={config.address} onChange={e => updateField('address', e.target.value)} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="setting-address" />
                </div>
                <div>
                  <label className="font-body text-sm text-[#75664d] mb-1 block">Horario</label>
                  <input value={config.opening_hours} onChange={e => updateField('opening_hours', e.target.value)} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="setting-hours" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment & Delivery */}
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
            <CardContent className="p-6">
              <h3 className="font-heading text-lg font-semibold text-[#46371c] mb-4 flex items-center gap-2"><CreditCard className="w-5 h-5" /> Pagos y Entregas</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-body text-sm text-[#75664d] mb-1 block">Depósito (%)</label>
                    <input type="number" value={config.deposit_percentage} onChange={e => updateField('deposit_percentage', Number(e.target.value))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="setting-deposit" />
                  </div>
                  <div>
                    <label className="font-body text-sm text-[#75664d] mb-1 block">Pedido mínimo (COP)</label>
                    <input type="number" value={config.min_order} onChange={e => updateField('min_order', Number(e.target.value))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="setting-min-order" />
                  </div>
                </div>
                <div>
                  <label className="font-body text-sm text-[#75664d] mb-1 block">Costo de envío por defecto (COP)</label>
                  <input type="number" value={config.delivery_fee} onChange={e => updateField('delivery_fee', Number(e.target.value))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="setting-delivery-fee" />
                </div>
                <div className="space-y-3 pt-2">
                  {[
                    { key: 'delivery_enabled', label: 'Entregas a domicilio' },
                    { key: 'whatsapp_enabled', label: 'WhatsApp activo' },
                    { key: 'rappi_enabled', label: 'Rappi activo' },
                    { key: 'payment_transfer', label: 'Pago por transferencia' },
                    { key: 'payment_cash', label: 'Pago contra entrega' },
                    { key: 'accept_online_payments', label: 'Aceptar pagos en línea (pasarelas de pago)' },
                  ].map(toggle => (
                    <div key={toggle.key} className="flex items-center justify-between py-2">
                      <span className="font-body text-sm text-[#46371c]">{toggle.label}</span>
                      <Switch checked={config[toggle.key]} onCheckedChange={v => updateField(toggle.key, v)} data-testid={`toggle-${toggle.key}`} />
                    </div>
                  ))}
                </div>

                {config.payment_transfer && (
                  <div className="pt-3 border-t border-[#e5dfd3]/60 space-y-3">
                    <h4 className="font-heading text-sm font-semibold text-[#46371c]">Datos de Cuenta para Transferencia</h4>
                    <p className="font-body text-xs text-[#75664d]">Estos datos se mostrarán a los clientes al seleccionar "Trans bancaria" en el checkout.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block">Banco</label>
                        <input
                          value={config.bank_name || ''}
                          onChange={e => updateField('bank_name', e.target.value)}
                          placeholder="Ej. Bancolombia"
                          className="w-full h-9 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        />
                      </div>
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block">Tipo de Cuenta</label>
                        <input
                          value={config.bank_account_type || ''}
                          onChange={e => updateField('bank_account_type', e.target.value)}
                          placeholder="Ej. Cuenta de Ahorros"
                          className="w-full h-9 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        />
                      </div>
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block">Número de Cuenta</label>
                        <input
                          value={config.bank_account_number || ''}
                          onChange={e => updateField('bank_account_number', e.target.value)}
                          placeholder="Ej. 123-456789-01"
                          className="w-full h-9 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        />
                      </div>
                      <div>
                        <label className="font-body text-xs text-[#75664d] mb-1 block">Titular de la Cuenta</label>
                        <input
                          value={config.bank_account_holder || ''}
                          onChange={e => updateField('bank_account_holder', e.target.value)}
                          placeholder="Ej. Keiks Repostería S.A.S"
                          className="w-full h-9 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Gateways Card */}
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl lg:col-span-2 mt-2">
            <CardContent className="p-6">
              <h3 className="font-heading text-lg font-semibold text-[#46371c] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" /> Pasarelas de Pago (Wompi y ePayco)
              </h3>
              
              <div className="mb-6 max-w-md">
                <label className="font-body text-sm text-[#75664d] mb-1.5 block">Pasarela Activa para Clientes</label>
                <Select value={config.active_gateway} onValueChange={v => updateField('active_gateway', v)}>
                  <SelectTrigger className="rounded-xl border-[#e5dfd3]" data-testid="setting-gateway-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna (Solo Transferencia / Efectivo)</SelectItem>
                    <SelectItem value="wompi">Wompi (Recomendada en Colombia)</SelectItem>
                    <SelectItem value="epayco">ePayco</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4 border-t border-[#e5dfd3]/50">
                {/* Wompi */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-heading text-base font-semibold text-[#46371c]">Configuración de Wompi</h4>
                    <Badge className={config.active_gateway === 'wompi' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {config.active_gateway === 'wompi' ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </div>
                  <div>
                    <label className="font-body text-sm text-[#75664d] mb-1.5 block">Entorno / Modo</label>
                    <Select value={config.wompi_mode} onValueChange={v => updateField('wompi_mode', v)}>
                      <SelectTrigger className="rounded-xl border-[#e5dfd3]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Pruebas)</SelectItem>
                        <SelectItem value="production">Producción (Real)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block">Llave Pública Sandbox</label>
                      <input value={config.wompi_public_key_sandbox} onChange={e => updateField('wompi_public_key_sandbox', e.target.value)} placeholder="pub_test_..." className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" />
                    </div>
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block">Llave Privada Sandbox</label>
                      <input type="password" value={config.wompi_private_key_sandbox} onChange={e => updateField('wompi_private_key_sandbox', e.target.value)} placeholder="prv_test_..." className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block">Llave Pública Producción</label>
                      <input value={config.wompi_public_key_production} onChange={e => updateField('wompi_public_key_production', e.target.value)} placeholder="pub_prod_..." className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" />
                    </div>
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block">Llave Privada Producción</label>
                      <input type="password" value={config.wompi_private_key_production} onChange={e => updateField('wompi_private_key_production', e.target.value)} placeholder="prv_prod_..." className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" />
                    </div>
                  </div>
                </div>

                {/* ePayco */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-heading text-base font-semibold text-[#46371c]">Configuración de ePayco</h4>
                    <Badge className={config.active_gateway === 'epayco' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {config.active_gateway === 'epayco' ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </div>
                  <div>
                    <label className="font-body text-sm text-[#75664d] mb-1.5 block">Entorno / Modo</label>
                    <Select value={config.epayco_mode} onValueChange={v => updateField('epayco_mode', v)}>
                      <SelectTrigger className="rounded-xl border-[#e5dfd3]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Pruebas)</SelectItem>
                        <SelectItem value="production">Producción (Real)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block">Llave Pública Sandbox</label>
                      <input value={config.epayco_public_key_sandbox} onChange={e => updateField('epayco_public_key_sandbox', e.target.value)} placeholder="Public Key" className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" />
                    </div>
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block">Llave Privada Sandbox</label>
                      <input type="password" value={config.epayco_private_key_sandbox} onChange={e => updateField('epayco_private_key_sandbox', e.target.value)} placeholder="Private Key" className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block">Llave Pública Producción</label>
                      <input value={config.epayco_public_key_production} onChange={e => updateField('epayco_public_key_production', e.target.value)} placeholder="Public Key" className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" />
                    </div>
                    <div>
                      <label className="font-body text-xs text-[#75664d] mb-1 block">Llave Privada Producción</label>
                      <input type="password" value={config.epayco_private_key_production} onChange={e => updateField('epayco_private_key_production', e.target.value)} placeholder="Private Key" className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Delivery Zones */}
      {activeTab === 'zones' && (
        <div className="space-y-6 animate-fade-in" data-testid="delivery-zones-section">
          {isAddingZone ? (
            <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <button onClick={() => setIsAddingZone(false)} className="text-[#75664d] hover:text-[#46371c]"><ArrowLeft className="w-5 h-5" /></button>
                  <h3 className="font-heading text-lg font-semibold text-[#46371c]">
                    {editingZone ? 'Editar Zona de Envío' : 'Nueva Zona de Envío'}
                  </h3>
                </div>

                <form onSubmit={saveZone} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="font-body text-sm text-[#75664d] mb-1 block">Nombre de la Zona *</label>
                      <input
                        value={zoneForm.name}
                        onChange={e => handleZoneFormChange('name', e.target.value)}
                        placeholder="Ej: BELLO (Bellavista y La Cumbre)"
                        className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        required
                        data-testid="zone-name-input"
                      />
                    </div>
                    <div>
                      <label className="font-body text-sm text-[#75664d] mb-1 block">Ruta asignada</label>
                      <input
                        value={zoneForm.route}
                        onChange={e => handleZoneFormChange('route', e.target.value)}
                        placeholder="Ej: Ruta 1"
                        className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                        data-testid="zone-route-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="font-body text-sm text-[#75664d] mb-1 block">Sede de Despacho *</label>
                      <Select value={zoneForm.store_id} onValueChange={v => handleZoneFormChange('store_id', v)}>
                        <SelectTrigger className="rounded-xl border-[#e5dfd3]" data-testid="zone-store-select">
                          <SelectValue placeholder="Seleccione una sede" />
                        </SelectTrigger>
                        <SelectContent>
                          {stores.map(store => (
                            <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="font-body text-sm text-[#75664d] mb-1 block">Costo Tortas Creativas (COP)</label>
                        <input
                          type="number"
                          value={zoneForm.delivery_creative}
                          onChange={e => handleZoneFormChange('delivery_creative', Number(e.target.value))}
                          className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                          data-testid="zone-creative-fee"
                        />
                      </div>
                      <div>
                        <label className="font-body text-sm text-[#75664d] mb-1 block">Costo Tortas Normales (COP)</label>
                        <input
                          type="number"
                          value={zoneForm.delivery_normal}
                          onChange={e => handleZoneFormChange('delivery_normal', Number(e.target.value))}
                          className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                          data-testid="zone-normal-fee"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="font-body text-sm font-semibold text-[#46371c] mb-2 block">Horario de Domicilios Permitidos en esta Zona</label>
                    <div className="grid grid-cols-3 gap-3 bg-white p-4 rounded-xl border border-[#e5dfd3]">
                      <div className="flex items-center justify-between">
                        <span className="font-body text-xs text-[#75664d]">9:00 AM - 12:00 M</span>
                        <Switch checked={zoneForm.schedule_9_12} onCheckedChange={v => handleZoneFormChange('schedule_9_12', v)} data-testid="zone-schedule-9-12" />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-body text-xs text-[#75664d]">12:00 M - 3:00 PM</span>
                        <Switch checked={zoneForm.schedule_12_3} onCheckedChange={v => handleZoneFormChange('schedule_12_3', v)} data-testid="zone-schedule-12-3" />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-body text-xs text-[#75664d]">3:00 PM - 6:00 PM</span>
                        <Switch checked={zoneForm.schedule_3_6} onCheckedChange={v => handleZoneFormChange('schedule_3_6', v)} data-testid="zone-schedule-3-6" />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setIsAddingZone(false)} className="rounded-full font-body text-sm text-[#75664d]">Cancelar</Button>
                    <Button type="submit" className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body text-sm flex items-center gap-1" data-testid="submit-zone-btn">
                      <Save className="w-4 h-4" /> Guardar Zona
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-heading text-lg font-semibold text-[#46371c]">Listado de Zonas de Domicilio</h3>
                <Button onClick={openAddZone} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body text-sm flex items-center gap-1.5" data-testid="add-zone-btn">
                  <Plus className="w-4 h-4" /> Nueva Zona
                </Button>
              </div>

              {zonesLoading ? (
                <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
              ) : zones.length === 0 ? (
                <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl p-8 text-center text-[#75664d] font-body text-sm">
                  No hay zonas de envío configuradas. Agrega una nueva zona para calcular domicilios en Checkout.
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {zones.map(zone => (
                    <Card key={zone.id} className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl shadow-sm hover:shadow-md transition-all">
                      <CardContent className="p-5 space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-heading text-base font-bold text-[#46371c]">{zone.name}</h4>
                            <p className="font-body text-xs text-[#75664d] flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3 text-[#a0d5ba]" />Sede: {zone.store_name} | {zone.route || 'Sin Ruta'}</p>
                          </div>
                          <div className="flex gap-1.5">
                            <button onClick={() => openEditZone(zone)} className="p-1.5 rounded-lg border border-[#e5dfd3] hover:bg-[#f4f1ea] text-[#46371c]" title="Editar"><Edit className="w-3.5 h-3.5" /></button>
                            <button onClick={() => deleteZone(zone.id)} className="p-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-600" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>

                        <div className="border-t border-[#e5dfd3]/40 pt-2.5 grid grid-cols-2 gap-3 text-xs font-body">
                          <div>
                            <p className="text-[#75664d]">Costo Envío:</p>
                            <p className="font-bold text-[#46371c]">Normal: <span className="font-medium">{formatPrice(zone.delivery_normal)}</span></p>
                            <p className="font-bold text-[#46371c]">Creativa: <span className="font-medium">{formatPrice(zone.delivery_creative)}</span></p>
                          </div>
                          <div>
                            <p className="text-[#75664d]">Horarios de Domicilio:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {zone.schedule_9_12 ? <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px] scale-90 px-1 py-0 font-normal">9am-12m</Badge> : <Badge variant="outline" className="text-[10px] scale-90 px-1 py-0 font-normal opacity-40">9am-12m</Badge>}
                              {zone.schedule_12_3 ? <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px] scale-90 px-1 py-0 font-normal">12m-3pm</Badge> : <Badge variant="outline" className="text-[10px] scale-90 px-1 py-0 font-normal opacity-40">12m-3pm</Badge>}
                              {zone.schedule_3_6 ? <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px] scale-90 px-1 py-0 font-normal">3pm-6pm</Badge> : <Badge variant="outline" className="text-[10px] scale-90 px-1 py-0 font-normal opacity-40">3pm-6pm</Badge>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab: Banners */}
      {activeTab === 'banners' && (
        <div className="space-y-6 animate-fade-in" data-testid="banners-section">
          {/* Main Full Banners Section */}
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
            <CardContent className="p-6 space-y-6">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#46371c]">Banners Completos de Inicio</h3>
                  <p className="font-body text-sm text-[#75664d] mt-1">Crea banners personalizados para el inicio con títulos, descripciones, botones de llamada a la acción (CTA) y tipo Jumbotron (100% de ancho).</p>
                </div>
                {editingBannerIndex === null && (
                  <Button onClick={openAddBanner} className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-full font-body text-xs font-bold flex items-center gap-1.5 shrink-0">
                    <Plus className="w-4 h-4" /> Agregar Banner
                  </Button>
                )}
              </div>

              {editingBannerIndex !== null ? (
                <form onSubmit={saveBanner} className="space-y-4 border border-[#e5dfd3]/60 bg-[#fdfcfb] rounded-2xl p-5">
                  <div className="flex justify-between items-center border-b border-[#e5dfd3]/60 pb-3 mb-4">
                    <h4 className="font-heading text-sm font-bold text-[#46371c]">
                      {editingBannerIndex === -1 ? 'Crear Nuevo Banner Completo' : `Editar Banner #${editingBannerIndex + 1}`}
                    </h4>
                    <button type="button" onClick={() => setEditingBannerIndex(null)} className="p-1 text-gray-400 hover:text-gray-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-4">
                      {/* Image URL & File Upload */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#46371c]/70">Imagen del Banner *</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={bannerForm.image}
                            onChange={(e) => setBannerForm(prev => ({ ...prev, image: e.target.value }))}
                            placeholder="URL de la imagen (ej: https://...)"
                            className="flex-1 h-10 px-3 py-2 text-sm bg-white border border-[#e5dfd3] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#a0d5ba]"
                          />
                          <label className="bg-[#46371c] hover:bg-[#2d2312] text-white px-4 h-10 rounded-lg flex items-center justify-center cursor-pointer transition-colors text-xs font-bold shrink-0">
                            <Upload className="w-4 h-4" />
                            <input type="file" onChange={handleBannerFormImageUpload} accept="image/*" className="hidden" />
                          </label>
                        </div>
                      </div>

                      {/* Title */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#46371c]/70">Título</label>
                        <input
                          type="text"
                          value={bannerForm.title}
                          onChange={(e) => setBannerForm(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Título del Banner (ej: Pedir un deseo no es lo mismo sin una torta)"
                          className="w-full h-10 px-3 py-2 text-sm bg-white border border-[#e5dfd3] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#a0d5ba]"
                        />
                      </div>

                      {/* Subtitle */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#46371c]/70">Descripción / Subtítulo</label>
                        <textarea
                          value={bannerForm.subtitle}
                          onChange={(e) => setBannerForm(prev => ({ ...prev, subtitle: e.target.value }))}
                          placeholder="Texto descriptivo corto..."
                          rows={3}
                          className="w-full px-3 py-2 text-sm bg-white border border-[#e5dfd3] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#a0d5ba]"
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* CTA Button Text */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#46371c]/70">Texto del Botón Principal (CTA)</label>
                        <input
                          type="text"
                          value={bannerForm.cta_text}
                          onChange={(e) => setBannerForm(prev => ({ ...prev, cta_text: e.target.value }))}
                          placeholder="Ej: ¿Cómo hago un pedido?"
                          className="w-full h-10 px-3 py-2 text-sm bg-white border border-[#e5dfd3] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#a0d5ba]"
                        />
                      </div>

                      {/* CTA Button Link */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#46371c]/70">Enlace del Botón Principal</label>
                        <input
                          type="text"
                          value={bannerForm.cta_link}
                          onChange={(e) => setBannerForm(prev => ({ ...prev, cta_link: e.target.value }))}
                          placeholder="Ej: /productos, video, o WhatsApp"
                          className="w-full h-10 px-3 py-2 text-sm bg-white border border-[#e5dfd3] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#a0d5ba]"
                        />
                      </div>

                      {/* Jumbotron Toggle */}
                      <div className="flex items-center justify-between p-3 bg-white border border-[#e5dfd3] rounded-xl">
                        <div>
                          <p className="font-heading text-xs font-bold text-[#46371c]">Estilo Jumbotron (100% Ancho)</p>
                          <p className="font-body text-[10px] text-[#75664d]">La imagen ocupará el 100% del fondo y el texto se superpondrá.</p>
                        </div>
                        <Switch
                          checked={bannerForm.is_jumbotron}
                          onCheckedChange={(checked) => setBannerForm(prev => ({ ...prev, is_jumbotron: checked }))}
                        />
                      </div>

                      {/* Image Preview */}
                      {bannerForm.image && (
                        <div className="space-y-2">
                          <label className="block text-xs font-bold uppercase tracking-wider text-[#46371c]/70">Vista Previa</label>
                          <div className="relative aspect-video rounded-xl overflow-hidden border border-[#e5dfd3]">
                            <img src={bannerForm.image} alt="Preview" className="w-full h-full object-cover" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-[#e5dfd3]/60">
                    <Button type="button" onClick={() => setEditingBannerIndex(null)} variant="outline" className="rounded-full font-body text-xs font-bold">
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-full font-body text-xs font-bold">
                      Guardar Banner
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="border-t border-[#e5dfd3]/40 pt-4">
                  <h4 className="font-heading text-sm font-bold text-[#46371c] mb-3">Banners Configurados ({config.banners?.length || 0})</h4>
                  {(!config.banners || config.banners.length === 0) ? (
                    <div className="text-center py-8 border border-dashed border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
                      <p className="font-body text-sm text-[#75664d] italic">No hay banners completos configurados.</p>
                      <Button onClick={openAddBanner} className="mt-3 bg-[#46371c] hover:bg-[#2d2312] text-white rounded-full font-body text-xs font-bold">
                        Crear Primer Banner
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {config.banners.map((banner, idx) => (
                        <Card key={idx} className="relative group rounded-xl overflow-hidden border border-[#e5dfd3] bg-white shadow-sm hover:shadow-md transition-all">
                          <div className="aspect-video relative">
                            <img src={banner.image} alt={`Banner ${idx + 1}`} className="w-full h-full object-cover" />
                            <div className="absolute top-2 left-2 flex gap-1.5">
                              <Badge className="bg-[#46371c] text-[#fcfbf9]">#{idx + 1}</Badge>
                              {banner.is_jumbotron ? (
                                <Badge className="bg-[#d63384] text-white">Jumbotron (100%)</Badge>
                              ) : (
                                <Badge className="bg-[#a0d5ba] text-[#2d523c]">Split (50/50)</Badge>
                              )}
                            </div>
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEditBanner(banner, idx)}
                                className="bg-white hover:bg-gray-100 text-[#46371c] p-2 rounded-full shadow transition-all hover:scale-105"
                                title="Editar Banner"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeBanner(idx)}
                                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow transition-all hover:scale-105"
                                  title="Eliminar Banner"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="p-4 space-y-1">
                            <h5 className="font-heading text-sm font-bold text-[#46371c] truncate">{banner.title || 'Sin Título'}</h5>
                            <p className="font-body text-xs text-[#75664d] line-clamp-2">{banner.subtitle || 'Sin Descripción'}</p>
                            {banner.cta_text && (
                              <div className="pt-2 flex gap-2 items-center text-[10px] font-body text-[#75664d]">
                                <span className="font-bold text-[#46371c]">Botón:</span>
                                <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200">{banner.cta_text} &rarr; {banner.cta_link}</span>
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legacy Simple Images Section as accordion / fallback */}
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl opacity-80">
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="font-heading text-sm font-semibold text-[#46371c]">Carrusel de Imágenes Simples (Legacy)</h3>
                <p className="font-body text-xs text-[#75664d] mt-1">Imágenes simples de respaldo en caso de no utilizar banners interactivos.</p>
              </div>

              {editingBannerIndex === null && (
                <>
                  <div className="flex items-center gap-4">
                    <label className="bg-gray-200 hover:bg-gray-300 text-[#46371c] rounded-full px-4 py-2 font-body text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> Subir Imagen Simple
                      <input type="file" onChange={handleBannerUpload} accept="image/*" className="hidden" />
                    </label>
                  </div>

                  <div className="border-t border-[#e5dfd3]/60 pt-4">
                    <h4 className="font-heading text-[11px] font-bold text-[#46371c] mb-3">Imágenes Actuales ({config.banner_images?.length || 0})</h4>
                    {(!config.banner_images || config.banner_images.length === 0) ? (
                      <p className="font-body text-[11px] text-[#75664d] italic">No hay imágenes simples.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                        {config.banner_images.map((url, idx) => (
                          <div key={idx} className="relative group rounded-lg overflow-hidden border border-[#e5dfd3] bg-white aspect-video shadow-sm">
                            <img src={url} alt={`Simple Banner ${idx + 1}`} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <button
                                type="button"
                                onClick={() => removeBannerImage(idx)}
                                className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-full shadow"
                                title="Eliminar"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Flavors and Fillings */}
      {activeTab === 'flavors_fillings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in" data-testid="flavors-fillings-section">
          {/* Sabores */}
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#46371c]">Sabores Globales</h3>
                  <p className="font-body text-xs text-[#75664d] mt-1">Configura los sabores que pueden habilitarse en los productos.</p>
                </div>
                {editingFlavorIndex === null && (
                  <Button onClick={openAddFlavor} className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-full font-body text-xs font-bold">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Sabor
                  </Button>
                )}
              </div>

              {editingFlavorIndex !== null ? (
                <form onSubmit={saveFlavor} className="space-y-3 p-4 bg-white border border-[#e5dfd3] rounded-xl">
                  <h4 className="font-heading text-xs font-bold text-[#46371c]">{editingFlavorIndex === -1 ? 'Agregar Sabor' : 'Editar Sabor'}</h4>
                  <div>
                    <label className="block text-[10px] font-bold text-[#46371c]">Nombre</label>
                    <input type="text" required value={flavorForm.name} onChange={e => setFlavorForm(prev => ({ ...prev, name: e.target.value }))} className="w-full h-8 px-2 text-xs border border-[#e5dfd3] rounded" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-[#46371c]">Precio Extra</label>
                    <input type="number" required value={flavorForm.price} onChange={e => setFlavorForm(prev => ({ ...prev, price: Number(e.target.value) }))} className="w-full h-8 px-2 text-xs border border-[#e5dfd3] rounded" />
                  </div>
                  <label className="flex items-center gap-2 font-body text-xs text-[#46371c] cursor-pointer">
                    <input type="checkbox" checked={flavorForm.is_refrigerated} onChange={e => setFlavorForm(prev => ({ ...prev, is_refrigerated: e.target.checked }))} className="rounded" />
                    ¿Es sabor refrigerado? (Deshabilita relleno)
                  </label>
                  <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditingFlavorIndex(null)} className="h-8 text-xs flex-1">Cancelar</Button>
                    <Button type="submit" size="sm" className="h-8 text-xs flex-1 bg-[#46371c] text-white">Guardar</Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {(!config.flavors || config.flavors.length === 0) ? (
                    <p className="text-xs text-[#75664d] italic">No hay sabores creados.</p>
                  ) : (
                    config.flavors.map((f, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-[#f4f1ea]/40 border border-[#e5dfd3] rounded-lg text-xs font-body text-[#46371c]">
                        <div>
                          <p className="font-semibold">{f.name} {f.is_refrigerated && <Badge className="bg-blue-100 text-blue-800 text-[9px] ml-1">Refrigerado</Badge>}</p>
                          <p className="text-[10px] text-[#75664d]">{formatPrice(f.price || 0)} extra</p>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => openEditFlavor(idx)} className="p-1 hover:bg-gray-100 rounded text-gray-500"><Edit className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deleteFlavor(idx)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rellenos */}
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#46371c]">Rellenos Globales</h3>
                  <p className="font-body text-xs text-[#75664d] mt-1">Configura los rellenos que pueden habilitarse en los productos.</p>
                </div>
                {editingFillingIndex === null && (
                  <Button onClick={openAddFilling} className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-full font-body text-xs font-bold">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Relleno
                  </Button>
                )}
              </div>

              {editingFillingIndex !== null ? (
                <form onSubmit={saveFilling} className="space-y-3 p-4 bg-white border border-[#e5dfd3] rounded-xl">
                  <h4 className="font-heading text-xs font-bold text-[#46371c]">{editingFillingIndex === -1 ? 'Agregar Relleno' : 'Editar Relleno'}</h4>
                  <div>
                    <label className="block text-[10px] font-bold text-[#46371c]">Nombre</label>
                    <input type="text" required value={fillingForm.name} onChange={e => setFillingForm(prev => ({ ...prev, name: e.target.value }))} className="w-full h-8 px-2 text-xs border border-[#e5dfd3] rounded" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-[#46371c]">Precio Extra</label>
                    <input type="number" required value={fillingForm.price} onChange={e => setFillingForm(prev => ({ ...prev, price: Number(e.target.value) }))} className="w-full h-8 px-2 text-xs border border-[#e5dfd3] rounded" />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditingFillingIndex(null)} className="h-8 text-xs flex-1">Cancelar</Button>
                    <Button type="submit" size="sm" className="h-8 text-xs flex-1 bg-[#46371c] text-white">Guardar</Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {(!config.fillings || config.fillings.length === 0) ? (
                    <p className="text-xs text-[#75664d] italic">No hay rellenos creados.</p>
                  ) : (
                    config.fillings.map((fill, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-[#f4f1ea]/40 border border-[#e5dfd3] rounded-lg text-xs font-body text-[#46371c]">
                        <div>
                          <p className="font-semibold">{fill.name}</p>
                          <p className="text-[10px] text-[#75664d]">{formatPrice(fill.price || 0)} extra</p>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => openEditFilling(idx)} className="p-1 hover:bg-gray-100 rounded text-gray-500"><Edit className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deleteFilling(idx)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Complements */}
      {activeTab === 'complements' && (
        <div className="space-y-6 animate-fade-in" data-testid="complements-section">
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
            <CardContent className="p-6 space-y-6">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#46371c]">Complementos de Productos</h3>
                  <p className="font-body text-sm text-[#75664d] mt-1">Administra velas, letreros, porciones extra y otros complementos que los clientes pueden añadir a sus pedidos.</p>
                </div>
                {editingComplementIndex === null && (
                  <Button onClick={openAddComplement} className="bg-[#46371c] hover:bg-[#2d2312] text-white rounded-full font-body text-xs font-bold flex items-center gap-1.5 shrink-0">
                    <Plus className="w-4 h-4" /> Agregar Complemento
                  </Button>
                )}
              </div>

              {editingComplementIndex !== null ? (
                <form onSubmit={saveComplement} className="space-y-4 border border-[#e5dfd3]/60 bg-[#fdfcfb] rounded-2xl p-5">
                  <div className="flex justify-between items-center border-b border-[#e5dfd3]/60 pb-3 mb-4">
                    <h4 className="font-heading text-sm font-bold text-[#46371c]">
                      {editingComplementIndex === -1 ? 'Crear Nuevo Complemento' : `Editar Complemento #${editingComplementIndex + 1}`}
                    </h4>
                    <button type="button" onClick={() => setEditingComplementIndex(null)} className="p-1 text-gray-400 hover:text-gray-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-4">
                      {/* Name */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#46371c]/70">Nombre del Complemento *</label>
                        <input
                          type="text"
                          required
                          value={complementForm.name}
                          onChange={(e) => setComplementForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Ej: Vela Volcán, Letrero Feliz Cumpleaños"
                          className="w-full h-10 px-3 py-2 text-sm bg-white border border-[#e5dfd3] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#a0d5ba]"
                        />
                      </div>

                      {/* Price */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#46371c]/70">Precio Adicional (COP) *</label>
                        <input
                          type="number"
                          required
                          value={complementForm.price}
                          onChange={(e) => setComplementForm(prev => ({ ...prev, price: Number(e.target.value) }))}
                          placeholder="Ej: 5000"
                          className="w-full h-10 px-3 py-2 text-sm bg-white border border-[#e5dfd3] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#a0d5ba]"
                        />
                      </div>

                      {/* Image Upload */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#46371c]/70">Imagen del Complemento</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={complementForm.image}
                            onChange={(e) => setComplementForm(prev => ({ ...prev, image: e.target.value }))}
                            placeholder="URL de la imagen (opcional)"
                            className="flex-1 h-10 px-3 py-2 text-sm bg-white border border-[#e5dfd3] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#a0d5ba]"
                          />
                          <label className="bg-[#46371c] hover:bg-[#2d2312] text-white px-4 h-10 rounded-lg flex items-center justify-center cursor-pointer transition-colors text-xs font-bold shrink-0">
                            <Upload className="w-4 h-4" />
                            <input type="file" onChange={handleComplementImageUpload} accept="image/*" className="hidden" />
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Checkboxes for features */}
                      <div className="space-y-3 pt-6">
                        <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={complementForm.allow_color}
                            onChange={(e) => setComplementForm(prev => ({ ...prev, allow_color: e.target.checked }))}
                            className="rounded"
                          />
                          Permitir Selección de Color
                        </label>
                        <label className="flex items-center gap-2 font-body text-sm text-[#46371c] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={complementForm.allow_quantity}
                            onChange={(e) => setComplementForm(prev => ({ ...prev, allow_quantity: e.target.checked }))}
                            className="rounded"
                          />
                          Permitir Seleccionar Cantidad
                        </label>
                      </div>

                      {complementForm.image && (
                        <div className="pt-2">
                          <p className="text-xs font-bold text-[#46371c]/70 mb-2">Vista Previa de la Imagen:</p>
                          <img src={complementForm.image} alt="Vista previa" className="w-32 h-32 object-cover rounded-lg border border-[#e5dfd3]" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-[#e5dfd3]/50">
                    <Button type="button" variant="outline" onClick={() => setEditingComplementIndex(null)} className="flex-1 rounded-full border-[#e5dfd3]">
                      Cancelar
                    </Button>
                    <Button type="submit" className="flex-1 bg-[#46371c] text-white hover:bg-[#2d2312] rounded-full">
                      Guardar Complemento
                    </Button>
                  </div>
                </form>
              ) : (
                <div>
                  {(!config.complements || config.complements.length === 0) ? (
                    <div className="text-center py-12 border-2 border-dashed border-[#e5dfd3] rounded-2xl">
                      <HelpCircle className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                      <p className="font-body text-[#75664d]">No hay complementos configurados aún. Haz clic en "Agregar Complemento" para empezar.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {config.complements.map((comp, idx) => (
                        <Card key={idx} className="relative group rounded-xl overflow-hidden border border-[#e5dfd3] bg-white shadow-sm hover:shadow-md transition-all">
                          <div className="aspect-video relative bg-gray-50 border-b border-[#e5dfd3]/50">
                            {comp.image ? (
                              <img src={comp.image} alt={comp.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-[#f4f1ea] text-[#75664d] font-body text-xs italic">
                                Sin Imagen
                              </div>
                            )}
                            <div className="absolute top-2 left-2">
                              <Badge className="bg-[#46371c] text-white">{formatPrice(comp.price)}</Badge>
                            </div>
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEditComplement(idx)}
                                className="bg-white hover:bg-gray-100 text-[#46371c] p-2 rounded-full shadow transition-all hover:scale-105"
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteComplement(idx)}
                                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow transition-all hover:scale-105"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="p-4 space-y-1 font-body text-xs text-[#75664d]">
                            <h5 className="font-heading text-sm font-bold text-[#46371c] truncate">{comp.name}</h5>
                            <div className="flex gap-2 pt-1 flex-wrap">
                              {comp.allow_color && <Badge className="bg-blue-100 text-blue-800 text-[9px]">Soporta Color</Badge>}
                              {comp.allow_quantity !== false && <Badge className="bg-green-100 text-green-800 text-[9px]">Soporta Cantidad</Badge>}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
